import React, { useEffect } from 'react'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { STAGE_LIMIT } from '../../../helpers/constants'; // Assuming same limit or generic limit
import TablePaginationActions from '../TablePaginationActions';

const columns = [
  { id: 'process_name', label: 'Process Name' },
  { id: 'company_name', label: 'Company Name' },
  { id: 'stage_name', label: 'Stage Name' },
  { id: 'sidebar_menu_id', label: 'Sidebar Menu Name' },
  { id: 'display_order', label: 'Display Order' },
  { id: 'is_active', label: 'Status' },
  { id: 'action', label: 'Actions' }
];

function AssignProcessTable(props) {
  const { is_loading,
    total_count,
    is_modal_open,
    handleUpdateModal,
    handleActivateDeactivate,
    data,
    setPage,
    page
  } = props;

  const rowsPerPage = STAGE_LIMIT
  const handleStatusChange = async (row) => {
    let newStatus = row.is_active ? 'dc' : 'ac';
    await handleActivateDeactivate({ assign_process_id: row.id, updated_data: newStatus });
  }

  const handleModal = async (c_action, assign_process_id) => {
    await handleUpdateModal(!is_modal_open, c_action, assign_process_id);
  };

  const handlePageChange = (event, newPage) => {
    // Dispatching is handled by parent or we need to dispatch here?
    // The container passed 'setAssignProcessPage' as 'setPage' prop. 
    // Wait, setPage prop is likely the action creator function itself if passed via mapDispatchToProps?
    // In AssignProcessContainer: setPage={setAssignProcessPage} which is an action creator passed via connect.
    // So distinct from dispatch logic. usage: setPage(newPage) -> returns action.
    // BUT since we are not connecting this component, we need to dispatch it?
    // OR the container passed a function that dispatches?
    // In Container: mapDispatchToProps = { setAssignProcessPage ... }
    // So this.props.setAssignProcessPage is a function that dispatches.
    // So simply calling props.setPage(newPage) works.
    setPage(newPage);
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer
        sx={{
          maxHeight: { xl: 800, md: 440 },
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "4px",
          },
        }}
      >
        <Table stickyHeader aria-label="sticky table"
          sx={{
            "& .MuiTableCell-stickyHeader": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              zIndex: 2,
            },
          }}
        >
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-root:not(:first-of-type)': {
                textAlign: 'left',
              }
            }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#eeeeee70",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {
              data && data.length > 0 ?
                data
                  .map((row, idx) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                        <TableCell align="left">
                          <Stack display='flex' flexDirection='row' alignItems='left'>
                            <Tooltip title={row.process_name || row.assign_process_name || "-"} arrow>
                              {row.process_name ? row.process_name : row.assign_process_name ? row.assign_process_name : '-'}
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          <Stack display='flex' flexDirection='row' alignItems='left'>
                            <Tooltip title={row.company_name || row.assign_process_name || "-"} arrow>
                              {row.company_name ? row.company_name : row.assign_process_name ? row.assign_process_name : '-'}
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          <Stack display='flex' flexDirection='row' alignItems='left'>
                            <Tooltip title={row.stage_name || row.assign_process_name || "-"} arrow>
                              {row.stage_name ? row.stage_name : row.assign_process_name ? row.assign_process_name : '-'}
                            </Tooltip>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <Stack display='flex' flexDirection='row' alignItems='left'>
                            <Tooltip title={row.sidebar_menu_name || "-"} arrow>
                              {row.sidebar_menu_name ? row.sidebar_menu_name : '-'}
                            </Tooltip>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <Stack display='flex' flexDirection='row' alignItems='left'>
                            <Tooltip title={row.display_order || "-"} arrow>
                              {row.display_order ? row.display_order : '-'}
                            </Tooltip>
                          </Stack>
                        </TableCell>


                        <TableCell align="left">
                          <Tooltip title={row.is_active ? 'Active' : 'Inactive' || "-"} arrow>
                            {row.is_active ? 'Active' : 'Inactive'}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Stack direction="row" justifyContent='left' spacing={2}>
                            <Tooltip title="Edit" arrow>
                              <IconButton type="button" aria-label="edit" color='primary' onClick={() => handleModal("UPDATE", row.id)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.5rem' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>

                              </IconButton>
                            </Tooltip>
                            <Tooltip title={row.is_active ? 'Active' : 'Inactive'} arrow>
                              <IconButton
                                type="button"
                                aria-label="toggle-status"
                                color="primary"
                                onClick={() => handleStatusChange(row)}
                              >
                                {row.is_active ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }) :
                !is_loading &&
                  (!data || data.length === 0) ? (
                  <TableRow>
                    <TableCell align='center' colSpan={5}>
                      No Record Found
                    </TableCell>
                  </TableRow>) :
                  (<TableRow>
                    <TableCell align='center' colSpan={5}>
                      loading....
                    </TableCell>
                  </TableRow>
                  )
            }
          </TableBody>
          {data && data.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={7} sx={{ padding: 0, borderBottom: 'none' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <TablePagination
                      component="div"
                      count={total_count}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[]}
                      ActionsComponent={TablePaginationActions}
                      sx={{
                        border: 'none',
                        '& .MuiTablePagination-toolbar': {
                          paddingRight: 2 // Ensure some padding on the right
                        }
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AssignProcessTable